import { Check, Star, ArrowRight } from "lucide-react";

const solutions = [
  {
    name: "Permis de construire",
    tag: "Projet simple",
    features: [
      "Constitution du dossier complet",
      "Plans 2D / 3D",
      "Vérification des règles d'urbanisme",
      "Préparation au dépôt en mairie",
    ],
    tagline: "Idéal pour un projet simple.",
    cta: "Demander mon devis",
    highlight: false,
  },
  {
    name: "Permis + étude de sol",
    tag: "Recommandé",
    features: [
      "Étude de sol adaptée à votre terrain (G1 à G5)",
      "Dossier complet optimisé",
      "Plans 2D / 3D",
      "Sécurisation du projet dès le départ",
      "Accompagnement personnalisé",
    ],
    tagline:
      "Solution recommandée pour éviter les erreurs, refus et surcoûts.",
    cta: "Parlons de votre projet",
    highlight: true,
  },
];

export default function Solutions() {
  return (
    <section
      id="solutions"
      data-testid="solutions-section"
      className="py-24 md:py-32"
    >
      <div className="container-wide">
        <div className="max-w-3xl mb-16 reveal">
          <div className="flex items-center gap-3 mb-6">
            <span className="rule" />
            <span className="overline">Deux solutions</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-medium leading-tight text-primary">
            Deux solutions pour{" "}
            <span className="italic font-light text-[hsl(var(--brand-terra))]">
              votre projet
            </span>
            .
          </h2>
          <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-xl">
            Choisissez l'accompagnement adapté à votre besoin.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-12 items-stretch">
          {/* Card 1 — Permis seul */}
          <article
            data-testid="solution-simple"
            className="reveal relative flex flex-col bg-white border border-border p-8 md:p-10 lg:col-span-5 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(47,45,42,0.15)] transition-all duration-300"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              {solutions[0].tag}
            </p>
            <h3 className="mt-3 font-display text-3xl md:text-4xl text-primary leading-tight">
              {solutions[0].name}
            </h3>

            <ul className="mt-8 space-y-3 flex-1">
              {solutions[0].features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-primary/85">
                  <span className="inline-flex h-5 w-5 items-center justify-center border border-border rounded-full text-primary/70 shrink-0 mt-0.5">
                    <Check size={12} strokeWidth={2} />
                  </span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <p className="mt-8 text-sm text-muted-foreground italic">
              👉 {solutions[0].tagline}
            </p>

            <a
              href="#contact"
              data-testid="solution-simple-cta"
              className="mt-8 group inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-7 py-4 text-sm font-medium tracking-wide rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary/90"
            >
              {solutions[0].cta}
              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </a>
          </article>

          {/* Card 2 — RECOMMANDÉ (plus grosse, plus visible) */}
          <article
            data-testid="solution-premium"
            className="reveal relative flex flex-col bg-primary text-primary-foreground border-t-4 border-t-[hsl(var(--brand-terra))] border-x border-b border-primary p-8 md:p-12 lg:col-span-7 lg:-mt-6 lg:-mb-6 premium-card"
          >
            {/* Recommandé badge — hors overflow */}
            <div className="absolute -top-4 left-8 md:left-10 inline-flex items-center gap-1.5 bg-[hsl(var(--brand-terra))] text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.22em] shadow-[0_10px_25px_-5px_rgba(219,112,1,0.55)] rounded-sm z-10">
              <Star size={13} strokeWidth={2.5} fill="currentColor" />
              Recommandé
            </div>

            {/* Decorative gradient — contenu séparé avec overflow-hidden */}
            <div
              aria-hidden
              className="absolute inset-0 overflow-hidden pointer-events-none"
            >
              <div className="absolute -top-40 -right-20 h-[380px] w-[380px] rounded-full bg-[radial-gradient(circle_at_center,hsl(var(--brand-terra)/0.25),transparent_70%)]" />
            </div>

            <div className="relative">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[hsl(var(--brand-terra))]">
                Solution complète
              </p>
              <h3 className="mt-3 font-display text-4xl md:text-5xl text-white leading-tight">
                Permis + étude de sol
              </h3>
            </div>

            <ul className="relative mt-10 space-y-3.5 flex-1">
              {solutions[1].features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-base text-white/95">
                  <span className="inline-flex h-6 w-6 items-center justify-center bg-[hsl(var(--brand-terra))] text-white rounded-full shrink-0 mt-0.5">
                    <Check size={14} strokeWidth={2.5} />
                  </span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <p className="relative mt-8 text-sm text-white/80 italic leading-relaxed border-l-2 border-[hsl(var(--brand-terra))] pl-4">
              👉 {solutions[1].tagline}
            </p>

            <a
              href="#contact"
              data-testid="solution-premium-cta"
              className="relative mt-10 group inline-flex items-center justify-center gap-2 bg-[hsl(var(--brand-terra))] text-white px-8 py-5 text-sm font-semibold tracking-wide rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:bg-[hsl(var(--brand-terra)/0.9)] hover:shadow-[0_20px_40px_-12px_rgba(219,112,1,0.55)]"
            >
              {solutions[1].cta}
              <ArrowRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </a>
          </article>
        </div>

        <p className="mt-14 text-center text-sm text-muted-foreground reveal">
          Chaque projet étant unique, un devis personnalisé vous sera proposé.
        </p>
      </div>
    </section>
  );
}
